import {
    contractConfig,
    cryptaniumVaultContractConfig,
    ETH,
} from "@config/contractsConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { formatUnits, maxUint256 } from "viem";
import { useAccount, useReadContracts } from "wagmi";
import useDappChain from "./useDappChain";

const useCryptaniumVaultAllowance = () => {
    const { selectedERC20Cryptanium } = useSelector((state: any) => state.user);
    const { isTransactionProcessing } = useSelector((state: any) => state.stake);
    const { address: walletAddress } = useAccount();
    const { chain } = useDappChain();

    // Use current chain configuration
    const chainConfig = contractConfig[chain.id];

    const token = useMemo(() => {
        if (!chainConfig || !cryptaniumVaultContractConfig[chain.id]) return null;

        let token: any =
            cryptaniumVaultContractConfig[chain.id].tokens?.[selectedERC20Cryptanium];

        if (!token) {
            token = chainConfig.tokens[selectedERC20Cryptanium];
        }

        if (selectedERC20Cryptanium === chainConfig.xtoken.symbol) {
            token = {
                ...chainConfig.xtoken,
                tags: [],
            };
        }

        return token;
    }, [chainConfig, selectedERC20Cryptanium, chain.id]);

    const allowanceContracts = useMemo(() => {
        if (!token || !cryptaniumVaultContractConfig[chain.id]) return [];

        const contracts = [];
        const vaultAddress =
            cryptaniumVaultContractConfig[chain.id]?.interactionPool.address;

        contracts.push({
            ...token,
            functionName: "allowance",
            args: [walletAddress, vaultAddress],
            chainId: chain.id,
        });

        return contracts;
    }, [token, walletAddress, chain.id]);

    const minDepositContracts = useMemo(() => {
        if (!cryptaniumVaultContractConfig[chain.id]) return [];

        const contracts = [];
        const vaultConfig =
            cryptaniumVaultContractConfig[chain.id]?.interactionPool || {};

        contracts.push({
            ...vaultConfig,
            functionName: "minDepositAmount",
            chainId: chain.id,
        });

        return contracts;
    }, [chain.id]);

    const { data, refetch: refetchAllowance } = useReadContracts({
        contracts: allowanceContracts,
        query: {
            enabled: !!walletAddress && allowanceContracts.length > 0,
            refetchInterval: 5_000,
        },
    });

    const { data: minDepositData } = useReadContracts({
        contracts: minDepositContracts,
        query: {
            enabled: !!cryptaniumVaultContractConfig[chain.id],
            refetchInterval: 5_000,
        },
    });

    useEffect(() => {
        if (!isTransactionProcessing) {
            refetchAllowance();
        }
    }, [isTransactionProcessing, refetchAllowance]);

    const allowanceData = useMemo(() => {
        if (data && walletAddress) {
            return {
                erc20Allowance:
                    selectedERC20Cryptanium !== ETH
                        ? +toFixedWithoutRounding(
                            data[0]?.result
                                ? formatUnits(data[0].result as bigint, token?.decimals || 18)
                                : "0",
                            8
                        )
                        : +toFixedWithoutRounding(
                            formatUnits(
                                maxUint256,
                                chainConfig?.nativeToken?.decimals || 18
                            ),
                            8
                        ),
                minDepositAmount: minDepositData?.[0]?.result
                    ? +toFixedWithoutRounding(
                        formatUnits(
                            minDepositData[0].result as bigint,
                            token?.decimals || 18
                        ),
                        8
                    )
                    : 0,
            };
        }
        return {
            erc20Allowance: 0,
            minDepositAmount: 0,
        };
    }, [
        data,
        walletAddress,
        selectedERC20Cryptanium,
        token,
        chainConfig,
        minDepositData,
    ]);

    return allowanceData;
};

export default useCryptaniumVaultAllowance;